import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Build, SupportAgent, ErrorOutline, HelpOutline } from '@mui/icons-material';
import { useAtom } from 'jotai';
import { chatMessagesState } from '../../state/chat/ChatMessagesState';
import { sendMessageToChatbot, useChatState } from '../../utils/client/messageUtils';
import { useAppTranslation } from '../../hooks/useAppTranslation';

const FALLBACK_STARTERS = {
    title: 'Start with a suggestion:',
    items: [
        { label: 'Fix issue', text: 'I have a problem with a machine.' },
        { label: 'Maintenance', text: 'How do I carry out the maintenance?' },
        { label: 'Contact support', text: 'I want to contact support.' },
        { label: 'Help', text: 'What can you do?' },
    ],
};

export function SmartStarter() {
    const [messages] = useAtom(chatMessagesState);
    const chatState = useChatState();
    const { isLoading } = chatState;
    const { t } = useAppTranslation();

    const userHasMessaged = messages.some((m) => m.sender === 'user');
    if (userHasMessaged) return null;

    const starters = [
        {
            label: t.smartStarterFixLabel || FALLBACK_STARTERS.items[0].label,
            icon: <Build fontSize='small' />,
            text: t.smartStarterFixText || FALLBACK_STARTERS.items[0].text,
        },
        {
            label: t.smartStarterMaintenanceLabel || FALLBACK_STARTERS.items[1].label,
            icon: <ErrorOutline fontSize='small' />,
            text: t.smartStarterMaintenanceText || FALLBACK_STARTERS.items[1].text,
        },
        {
            label: t.smartStarterSupportLabel || FALLBACK_STARTERS.items[2].label,
            icon: <SupportAgent fontSize='small' />,
            text: t.smartStarterSupportText || FALLBACK_STARTERS.items[2].text,
        },
        {
            label: t.smartStarterHelpLabel || FALLBACK_STARTERS.items[3].label,
            icon: <HelpOutline fontSize='small' />,
            text: t.smartStarterHelpText || FALLBACK_STARTERS.items[3].text,
        },
    ].filter((starter) => Boolean(starter.label && starter.text));

    const handleClick = (text) => {
        if (isLoading || !text) return;
        sendMessageToChatbot(text, chatState);
    };

    return (
        <Box display='flex' flexDirection='column' alignItems='center' gap={2} mb={4} mt={2}>
            <Box
                sx={(theme) => {
                    const isDark = theme.palette.mode === 'dark';
                    return {
                        px: 1.55,
                        py: 0.65,
                        borderRadius: 99,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.82)',
                        backdropFilter: 'blur(10px)',
                        border: isDark
                            ? '1px solid rgba(255,255,255,0.14)'
                            : '1px solid rgba(148, 163, 184, 0.28)',
                        boxShadow: isDark
                            ? '0 8px 20px rgba(0, 0, 0, 0.24)'
                            : '0 8px 20px rgba(15, 23, 42, 0.08)',
                    };
                }}
            >
                <Typography
                    variant='caption'
                    sx={(theme) => ({
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.94)' : 'rgba(15, 23, 42, 0.78)',
                        fontWeight: 700,
                        letterSpacing: '0.01em',
                    })}
                >
                    {t.smartStarterTitle || FALLBACK_STARTERS.title}
                </Typography>
            </Box>

            <Box display='flex' gap={1} flexWrap='wrap' justifyContent='center'>
                {starters.map((starter) => (
                    <Chip
                        key={`${starter.label}-${starter.text}`}
                        icon={starter.icon}
                        label={starter.label}
                        onClick={isLoading ? undefined : () => handleClick(starter.text)}
                        variant='outlined'
                        clickable={!isLoading}
                        sx={(theme) => {
                            const isDark = theme.palette.mode === 'dark';
                            return {
                                height: 36,
                                borderRadius: 999,
                                backgroundColor: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.86)',
                                color: 'rgba(15, 23, 42, 0.88)',
                                borderColor: isDark
                                    ? 'rgba(255,255,255,0.18)'
                                    : 'rgba(148, 163, 184, 0.42)',
                                backdropFilter: 'blur(8px)',
                                boxShadow: isDark
                                    ? '0 10px 22px rgba(0, 0, 0, 0.18)'
                                    : '0 10px 22px rgba(15, 23, 42, 0.06)',
                                opacity: isLoading ? 0.6 : 1,
                                '& .MuiChip-label': {
                                    paddingLeft: '10px',
                                    paddingRight: '14px',
                                    fontWeight: 500,
                                    lineHeight: '36px',
                                },
                                '& .MuiChip-icon': {
                                    marginLeft: '10px',
                                    marginRight: '-4px',
                                    fontSize: 18,
                                    color: 'rgba(51, 65, 85, 0.88)',
                                },
                                '&:hover': {
                                    backgroundColor: isDark ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.96)',
                                    borderColor: isDark
                                        ? 'rgba(255,255,255,0.26)'
                                        : 'rgba(100, 116, 139, 0.48)',
                                },
                            };
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}
